import type { SenderAction, SenderPriorityUsage } from "@/api/custom_types";
import { SenderPriorityUsageEnum } from "@/constants";
import { useStatusMap } from "@/hooks";
import { useTranslation } from "@/hooks/useTranslation";
import {
	useAssignSenderGroup,
	useBatchUpdateSenderPriority,
	useDeleteMultipleSenders,
	useGetAllGroups,
	useReloginMultipleSenders,
	useStopResumeMultipleSenders,
} from "@/services";
import { getSenderPriorityTranslation } from "@/utils/translations";
import {
	Button,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Icon,
	SmartSelect,
	Typography,
	toast,
} from "@st-marketplace/ui";
import { useCallback, useMemo, useState } from "react";
import type { SenderRow } from "./types";
import { useSendersCsvExport } from "./useSendersCsvExport";

export interface SendersBulkActionProps {
	items: SenderRow[];
	itemsIDs: string[];
	onClear: () => void;
}

type BulkActionType = "DELETE" | "PAUSE" | "RESUME" | "RELOGIN";
type BulkPriority = SenderPriorityUsage | "INHERITED";

const SendersBulkAction: React.FC<SendersBulkActionProps> = ({
	items,
	itemsIDs,
	onClear,
}) => {
	const { t } = useTranslation();
	const { senderStatusMap } = useStatusMap();
	const { exportSenders, isExporting } = useSendersCsvExport();

	const deleteMultipleSenders = useDeleteMultipleSenders();
	const stopResumeMultipleSenders = useStopResumeMultipleSenders();
	const reloginMultipleSenders = useReloginMultipleSenders();
	const assignSenderGroup = useAssignSenderGroup();
	const updateSenderPriority = useBatchUpdateSenderPriority();
	const { data: groups } = useGetAllGroups({});

	const [warningDialog, setWarningDialog] = useState<boolean>(false);
	const [deleteDialog, setDeleteDialog] = useState<boolean>(false);
	const [assignGroupDialog, setAssignGroupDialog] = useState(false);
	const [senderGroupId, setSenderGroupId] = useState("");
	const [priorityDialog, setPriorityDialog] = useState(false);
	const [selectedPriority, setSelectedPriority] = useState<BulkPriority | "">(
		"",
	);
	const [selectedAction, setSelectedAction] = useState<BulkActionType>("PAUSE");

	const allowedCounts = useMemo<Record<BulkActionType, number>>(() => {
		const counts: Record<BulkActionType, number> = {
			DELETE: 0,
			PAUSE: 0,
			RESUME: 0,
			RELOGIN: 0,
		};

		for (const item of items) {
			const status = senderStatusMap?.[item.status];
			if (status?.can_be_deleted) counts.DELETE++;
			if (status?.can_pause) counts.PAUSE++;
			if (status?.can_resume) counts.RESUME++;
			if (status?.can_relogin) counts.RELOGIN++;
		}
		return counts;
	}, [items, senderStatusMap]);

	const eligibleIds = useCallback(
		(action: BulkActionType) =>
			items
				.filter((item) => {
					const status = senderStatusMap?.[item.status];
					return (
						(action === "DELETE" && status?.can_be_deleted) ||
						(action === "PAUSE" && status?.can_pause) ||
						(action === "RESUME" && status?.can_resume) ||
						(action === "RELOGIN" && status?.can_relogin)
					);
				})
				.map((item) => item.id),
		[items, senderStatusMap],
	);

	const onDeleteMultipleSenders = () => {
		deleteMultipleSenders.mutate(
			{ body: eligibleIds("DELETE") },
			{
				onSuccess: () => {
					setDeleteDialog(false);
					setWarningDialog(false);
					onClear();
				},
			},
		);
	};

	const onStopResumeMultipleSenders = useCallback(
		(action: SenderAction) => {
			stopResumeMultipleSenders.mutate(
				{
					body: {
						action,
						sender_ids: eligibleIds(action),
					},
				},
				{
					onSuccess: () => {
						if (action === "PAUSE")
							toast.success(t("sender.action.successPauseSenders"));
						else toast.success(t("sender.action.successResumeSenders"));
						setWarningDialog(false);
						onClear();
					},
				},
			);
		},
		[stopResumeMultipleSenders, eligibleIds, t, onClear],
	);

	const handleReloginSenders = useCallback(() => {
		reloginMultipleSenders.mutate(
			{ body: eligibleIds("RELOGIN") },
			{
				onSuccess: () => {
					toast.success(
						t("sender.action.successReloginSenders", {
							value: eligibleIds("RELOGIN").length,
						}),
					);
					setWarningDialog(false);
					onClear();
				},
			},
		);
	}, [reloginMultipleSenders, eligibleIds, t, onClear]);

	const warningContent = useMemo(() => {
		switch (selectedAction) {
			case "DELETE":
				return {
					description: t("sender.action.deleteMessage", {
						value: allowedCounts.DELETE,
					}),
					confirmLabel: t("action.button.delete"),
					loading: false,
					onConfirm: () => {
						setWarningDialog(false);
						setDeleteDialog(true);
					},
				};
			case "PAUSE":
				return {
					description: t("sender.action.pauseMessage", {
						value: allowedCounts.PAUSE,
					}),
					confirmLabel: t("action.button.pause"),
					loading: stopResumeMultipleSenders.isPending,
					onConfirm: () => onStopResumeMultipleSenders("PAUSE"),
				};
			case "RESUME":
				return {
					description: t("sender.action.playMessage", {
						value: allowedCounts.RESUME,
					}),
					confirmLabel: t("action.button.play"),
					loading: stopResumeMultipleSenders.isPending,
					onConfirm: () => onStopResumeMultipleSenders("RESUME"),
				};
			case "RELOGIN":
				return {
					description: t("sender.action.reloginMessage", {
						value: allowedCounts.RELOGIN,
					}),
					confirmLabel: t("action.button.confirmRelogin"),
					loading: reloginMultipleSenders.isPending,
					onConfirm: handleReloginSenders,
				};
		}
	}, [
		selectedAction,
		allowedCounts,
		t,
		onStopResumeMultipleSenders,
		handleReloginSenders,
		stopResumeMultipleSenders.isPending,
		reloginMultipleSenders.isPending,
	]);

	const openWarningDialog = (action: BulkActionType) => {
		setSelectedAction(action);
		setWarningDialog(true);
	};

	const assignGroup = () => {
		assignSenderGroup.mutate(
			{ senderIds: itemsIDs, senderGroupId },
			{
				onSuccess: () => {
					toast.success(t("sender.action.successAssignGroup"));
					setAssignGroupDialog(false);
					setSenderGroupId("");
					onClear();
				},
			},
		);
	};
	const priorityItems = useMemo(
		() => [
			{ value: "INHERITED", label: t("sender.action.inheritPriority") },
			...Object.values(SenderPriorityUsageEnum).map((value) => ({
				value,
				label: getSenderPriorityTranslation(value, t).text.toString(),
			})),
		],
		[t],
	);
	const updatePriority = () => {
		if (!selectedPriority) return;
		updateSenderPriority.mutate(
			{
				senderIds: itemsIDs,
				priorityUsage:
					selectedPriority === "INHERITED" ? null : selectedPriority,
			},
			{
				onSuccess: () => {
					toast.success(
						t("sender.action.successPriority", { value: itemsIDs.length }),
					);
					setPriorityDialog(false);
					setSelectedPriority("");
					onClear();
				},
			},
		);
	};

	return (
		<>
			<div className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-secondary-border bg-background-300 p-1">
				<Button icon variant="text" color="ghost" size="sm" onClick={onClear}>
					<Icon name="close-large-line" size={14} />
				</Button>

				<div className="flex items-center px-2 py-1.5">
					<Typography variant="body-md" className="font-semibold">
						{t("sender.selectedSenders", { value: items.length })}
					</Typography>
				</div>

				<Button
					color="secondary"
					startIcon={<Icon name="sort-desc" />}
					onClick={() => setPriorityDialog(true)}
				>
					{t("sender.action.changePriority")}
				</Button>

				<Button
					color="secondary"
					startIcon={<Icon name="folder-line" />}
					disabled={!groups?.length}
					onClick={() => setAssignGroupDialog(true)}
				>
					{t("sender.action.assignGroup")}
				</Button>

				<Button
					color="secondary"
					startIcon={<Icon name="download-2-line" />}
					loading={isExporting}
					disabled={isExporting}
					onClick={() => exportSenders(itemsIDs)}
				>
					{t("action.button.exportCsv")}
				</Button>

				<Button
					color="secondary"
					startIcon={<Icon name="loop-left-fill" />}
					disabled={allowedCounts.RELOGIN === 0}
					onClick={() => openWarningDialog("RELOGIN")}
				>
					{t("action.relogin")}
				</Button>

				<Button
					color="secondary"
					startIcon={<Icon name="pause-circle-line" />}
					disabled={allowedCounts.PAUSE === 0}
					onClick={() => openWarningDialog("PAUSE")}
				>
					{t("action.pause")}
				</Button>

				<Button
					color="secondary"
					startIcon={<Icon name="play-circle-line" />}
					disabled={allowedCounts.RESUME === 0}
					onClick={() => openWarningDialog("RESUME")}
				>
					{t("action.play")}
				</Button>

				<Button
					color="danger"
					startIcon={<Icon name="delete-bin-line" />}
					disabled={allowedCounts.DELETE === 0}
					onClick={() => openWarningDialog("DELETE")}
				>
					{t("action.delete")}
				</Button>
			</div>

			<Dialog open={warningDialog} onOpenChange={setWarningDialog}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("action.areYouSure")}</DialogTitle>
					</DialogHeader>

					<div className="px-4">
						<div className="mb-6 rounded-md border border-primary-border bg-primary-300 p-4">
							<Typography variant="body-md" className="mb-2 text-primary-100">
								{t("sender.selectedSenders", { value: items.length })}
							</Typography>
							<Typography variant="body-md" className="text-primary-100">
								{t("sender.selectedSenders", {
									value: allowedCounts[selectedAction],
								})}
							</Typography>
						</div>
						<Typography variant="body-md">
							{warningContent.description}
						</Typography>
					</div>

					<DialogFooter
						buttons={{
							primary: {
								label: warningContent.confirmLabel,
								props: {
									loading: warningContent.loading,
									onClick: warningContent.onConfirm,
								},
							},
							secondary: {
								label: t("common.button.no"),
								props: { onClick: () => setWarningDialog(false) },
							},
						}}
					/>
				</DialogContent>
			</Dialog>

			<Dialog open={assignGroupDialog} onOpenChange={setAssignGroupDialog}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("sender.action.assignGroup")}</DialogTitle>
					</DialogHeader>
					<div className="px-4">
						<SmartSelect
							items={(groups ?? []).map((group) => ({
								value: group.id,
								label: group.name,
							}))}
							value={senderGroupId}
							onChange={setSenderGroupId}
							searchable
							className="w-full"
							placeholder={t("addSender.form.inputGroupName")}
						/>
					</div>
					<DialogFooter
						buttons={{
							primary: {
								label: t("sender.action.assignGroup"),
								props: {
									disabled: !senderGroupId,
									loading: assignSenderGroup.isPending,
									onClick: assignGroup,
								},
							},
							secondary: {
								label: t("common.button.cancel"),
								props: { onClick: () => setAssignGroupDialog(false) },
							},
						}}
					/>
				</DialogContent>
			</Dialog>

			<Dialog open={priorityDialog} onOpenChange={setPriorityDialog}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("sender.action.changePriority")}</DialogTitle>
					</DialogHeader>
					<div className="px-4">
						<Typography variant="body-md" className="mb-4">
							{t("sender.action.priorityMessage", { value: itemsIDs.length })}
						</Typography>
						<SmartSelect
							items={priorityItems}
							value={selectedPriority}
							onChange={(value) => setSelectedPriority(value as BulkPriority)}
							className="w-full"
							placeholder={t("sender.action.selectPriority")}
						/>
					</div>
					<DialogFooter
						buttons={{
							primary: {
								label: t("sender.action.changePriority"),
								props: {
									disabled: !selectedPriority,
									loading: updateSenderPriority.isPending,
									onClick: updatePriority,
								},
							},
							secondary: {
								label: t("common.button.cancel"),
								props: { onClick: () => setPriorityDialog(false) },
							},
						}}
					/>
				</DialogContent>
			</Dialog>

			<Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle
							trailingIcon="alert-line"
							iconClassName="text-danger-100"
						>
							{t("action.areYouSure")}
						</DialogTitle>
					</DialogHeader>

					<div className="px-4">
						<Typography variant="body-md">
							{t("sender.action.deleteMessage", {
								value: itemsIDs.length,
							})}
						</Typography>
					</div>

					<DialogFooter
						buttons={{
							primary: {
								label: t("action.button.delete"),
								props: {
									color: "danger",
									loading: deleteMultipleSenders.isPending,
									onClick: onDeleteMultipleSenders,
								},
							},
							secondary: {
								label: t("common.button.no"),
								props: { onClick: () => setDeleteDialog(false) },
							},
						}}
					/>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default SendersBulkAction;
